import React, { useEffect } from 'react';
import { Alert, Breadcrumb, Breadcrumbs, Heading, Spinner } from '@digitools/honest-react';
import styles from './CoverageDetails.scss';
import queryString from 'query-string';
import { useLanguage, } from 'status/hooks';
import useGet from '../../../../../hooks/useGet';
import { withRouter } from 'src/utils/withRouter';
import CoverageTabs from './CoverageTabs';
import { decode } from 'status/utilities/hash';
import EmployeeDetails from 'status/components/EmployeeDetails/EmployeeDetails';
import TeamViewEmployeeActions from './TeamViewEmployeeActions';
import EmployeeEligibilityDetails from './EmployeeEligibilityDetails';
import Notifications, { FunctionalArea } from '../../../../../components/Notifications/Notifications';
import useTealium from '../../../../../hooks/useTealium';
import { TEALIUM_PAGE_VIEW } from '../../../../../constants/tealiumConstants';
import { useLocation, useParams } from 'react-router-dom';
import useAuthentication from 'src/hooks/useAuthentication';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
import { getEnvVariable } from 'src/utils/envUtils';
import { trackBreadcrumbEvent, useHomePageBreadcrumbs } from 'src/components/Breadcrumbs';
import useTranslate from 'src/hooks/useTranslate';
import StatusText from '../../../i18n/StatusText';
import BreadcrumbText from '../../../i18n/BreadcrumbText';
import useStatusDataContext from 'src/packages/status/hooks/useStatusDataContext';
const EmployeeError = ({ error }) => (<Alert type={'error'}>
    {error.response && error.response.status === 404 ? ('No employee found.') : (<span>
        <strong>Error!</strong>&nbsp;Something went wrong when retrieving this employee.
      </span>)}
  </Alert>);
// Helper function to determine if we can show EOI
const showEOI = (userMeta, experience) => {
    if (experience === Experience.EMPLOYER) {
        return false;
    }
    const applicationPermissions = userMeta.applicationPermissions;
    return applicationPermissions.indexOf('statusEE.eoiStatus') > -1;
};
const FullCoverageDetails = ({ match, fromTeamView }) => {
    const location = useLocation();
    const parsed = queryString.parse(location.search);
    const { user } = useAuthentication();
    const { language } = useLanguage();
    const { experience } = useExperience();
    const { t } = useTranslate();
    const { statusHeader } = StatusText;
    const { viewStatus } = BreadcrumbText;
    const { trackView, trackEvent } = useTealium();
    const pathParams = useParams();
    //prevent nav while scrolled leaving you at the bottom
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    useEffect(() => {
        trackView({
            page_l3: TEALIUM_PAGE_VIEW.PAGE_L3.STATUS,
            page_l4: experience === Experience.EMPLOYER
                ? TEALIUM_PAGE_VIEW.PAGE_L4.STATUS.EMPLOYER_VIEW
                : TEALIUM_PAGE_VIEW.PAGE_L4.STATUS.EMPLOYEE_VIEW,
            page_l5: TEALIUM_PAGE_VIEW.PAGE_L5.STATUS.COVERAGE_DETAILS
        });
    }, []);
    const empNum = decode(pathParams.empNum);
    const params = { empNum };
    const { error: claimError, loading: loadingClaims, data: claimData, } = useGet({
        url: '/status-service/claims/search/searchByEmpNum',
        options: {
            params,
        },
        handleUnauthorized: true,
    });
    const { error: leaveError, loading: loadingLeaves, data: leaveData, } = useGet({
        url: '/status-service/leaves/search/searchByEmpNum',
        options: {
            params,
        },
        noInitial: user?.customerInContext.smallMid,
        handleUnauthorized: true,
    });
    const { error: employeeError, loading: loadingEmployee, data: employeeData, } = useGet({
        url: '/status-service/employee/search/searchByEmpNum',
        options: {
            params,
        },
    });
    const showEOITab = showEOI(user, experience);
    const { error: eoiError, loading: loadingEoi, data: eoiData, } = useGet({
        url: '/status-service/eoi/status',
        options: {
            params: {
                languageCode: language,
            },
        },
        noInitial: !showEOITab,
    }, [language]);
    const { codesLoading, definitionsLoading } = useStatusDataContext();
    const loading = loadingEoi || loadingClaims || loadingLeaves || loadingEmployee || codesLoading || definitionsLoading;
    if (loading) {
        return <Spinner id='full-coverage-details-spinner'/>;
    }
    if (employeeError) {
        return <EmployeeError error={employeeError}/>;
    }
    let to;
    if (parsed.search) {
        if (parsed.singleResult) {
            to = `/status/employer/search?search=${parsed.search}&searchCrit=${parsed.searchCrit}&from=singleResult`;
        }
        else {
            to = `/status/employer/search?search=${parsed.search}&searchCrit=${parsed.searchCrit}&from=detailPage`;
        }
    }
    else {
        to = '/status/employer/search';
    }
    return (<div className={styles.container}>
      <Breadcrumbs>
        {useHomePageBreadcrumbs()}
        {(experience === Experience.EMPLOYER && !fromTeamView) && <Breadcrumb onClick={() => trackBreadcrumbEvent('Search for claimant', trackEvent)} url={to}>Search for claimant</Breadcrumb>}
        {(experience === Experience.EMPLOYER && fromTeamView) && <Breadcrumb onClick={() => trackBreadcrumbEvent('Team view', trackEvent)} url={'/teamview'}>Team view</Breadcrumb>}
        <Breadcrumb>{t(viewStatus)}</Breadcrumb>
      </Breadcrumbs>
      <Notifications functionalArea={FunctionalArea.COVERAGE_DETAILS}/>
      <Heading data-testid='status-header'>{t(statusHeader)}</Heading>
      {/* If new coverage details turned off - show, otherwise - only show for ER users */}
      {(!getEnvVariable('statusNewCoverageDetails') || experience === Experience.EMPLOYER) && <EmployeeDetails empNum={empNum} empEmailAddress={employeeData.empEmailAddr}/>}  

      <EmployeeEligibilityDetails empId={employeeData.empId} empNum={empNum}/>
      {fromTeamView ? <TeamViewEmployeeActions empId={employeeData.empId}/> : null}
        <CoverageTabs claimData={claimData} leaveData={leaveData} employeeData={employeeData} eoiData={eoiData} eoiError={eoiError} showEOITab={showEOITab} empNum={empNum} 
    // prodAttCodes={productAttributeCodes}  comes from context now
    // statusDefinitionList={statusDefinitionList}  comes from context now
    claimError={claimError} leaveError={leaveError} //|| prodAttError || statusDefError  come from context now
     fromTeamView={fromTeamView}/>
      
    </div>);
};
export default withRouter(FullCoverageDetails);
